import { useRequiredContext } from "@redotech/react-util/context";
import { galeKycPath } from "@redotech/redo-conversion-hsa-common/gale-kyc-path";
import { hsaAdyenKycPath } from "@redotech/redo-conversion-hsa-common/hsa-adyen-kyc-path";
import { newWindow } from "@redotech/web-util/window";
import { UserContext } from "../app/user";
import { RedoAdminClientContext } from "../client/context";
import { createTeamAuth } from "../client/team";
import { REDO_MERCHANT_APP_URL } from "../config";

export function automationUrl(teamId: string, automationId: string): string {
  return `${REDO_MERCHANT_APP_URL}/stores/${teamId}/settings/oms/automations/${automationId}`;
}

export function conversionDashboardUrl(teamId: string): string {
  return `${REDO_MERCHANT_APP_URL}/stores/${teamId}/conversion/dashboard`;
}

/** Store-relative, for `useMerchantAppLogin`'s `next` param. */
export const LIVE_CHECKOUT_TREE_PATH =
  "/conversion/checkouts/?checkout-experience-page-tab=live-checkout-tree-view";

/** Store-relative, for `useMerchantAppLogin`'s `next` param. */
export const MARKETS_SETTINGS_PATH = "/conversion/markets";

/** A running storefront A/B test's results page, as the merchant's test list opens it; store-relative. */
export function storefrontAbTestResultsPath(testId: string): string {
  return `/returns/ab-tests/${testId}/results`;
}

// Stable link sent to a merchant to start Gale identity verification. Opening it
// (the merchant logs into their dashboard) mints a fresh, unexpired hosted
// onboarding link server-side and redirects there — see the merchant
// `startGaleKyc` RPC. Unlike the hosted link itself, this URL never expires.
export function galeKycRedirectUrl(teamId: string): string {
  return `${REDO_MERCHANT_APP_URL}${galeKycPath(teamId)}`;
}

/** The merchant's Verify your business page; each click there mints a fresh Adyen link. */
export function hsaAdyenKycUrl(teamId: string): string {
  return `${REDO_MERCHANT_APP_URL}${hsaAdyenKycPath(teamId)}`;
}

export function useMerchantAppLogin(): (teamId: string, next?: string) => void {
  const client = useRequiredContext(RedoAdminClientContext);
  const user = useRequiredContext(UserContext);

  return (teamId, next) => {
    newWindow(async () => {
      try {
        const auth = await createTeamAuth(client, { name: user.name, teamId });
        const url = new URL("/login/token", REDO_MERCHANT_APP_URL);
        url.searchParams.set("token", auth.authToken);
        if (next) {
          url.searchParams.set("next", next);
        }
        return url.toString();
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        alert((e as Error).message);
        throw e;
      }
    });
  };
}
